// ScanResourceId.tsx
import { useState } from 'react';
import {
    Box,
    Stack,
    Typography,
    Button,
    Divider,
} from '@mui/material';

import { useCommonStyles } from '../../styles/GenericStyle';
import ScanInput from '../ScanInput';
import ScanButton from '../spLot/ScanIcon';
import { black } from "../../colors";
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import SkipNextOutlinedIcon from '@mui/icons-material/SkipNextOutlined';
import { validateEquipment } from '../spLot/equipmentValidation';
import type { SplotStepEquipment } from '../../api/vegApl/SPLotModel';
import CleaningSkipConfirmDialog from '../common/CleaningSkipConfirmDialog';


export type ScanResourceIdProps = {
    onResourceScanned: (resourceId: string) => void;
    onBack: () => void;
    onSkip?: (skipReason?: string) => void;
    currentStepName?: string;
    currentStepEquipments?: SplotStepEquipment[];
    scanMode?: 'equipment' | 'treatment';
    isEquipmentMandatory?: boolean;
    mappedTreatments?: string[];
}

const normalizeStepName = (stepName?: string) => String(stepName ?? '').trim().toLowerCase();

const isScannedTreatmentStep = (stepName?: string) => {
    const normalized = normalizeStepName(stepName);
    if (normalized.includes('ferment')) return true;
    return /disinfection[^0-9]*([1-3])\b/.test(normalized);
};

export default function ScanResourceId({
    onResourceScanned,
    onBack,
    onSkip,
    currentStepName,
    currentStepEquipments = [],
    scanMode,
    isEquipmentMandatory = true,
    mappedTreatments = [],
}: ScanResourceIdProps) {

    const classes = useCommonStyles();
    const [scannedValue, setScannedValue] = useState("");
    const manualEdit = true;
    const [errorText, setErrorText] = useState("");
    const [scanInputKey, setScanInputKey] = useState(0);
    const [validResourceList, setValidResourceList] = useState<string[]>([]);
    const [isMultipleResourcesChecked] = useState(false);
    const [showSkipCleaningConfirm, setShowSkipCleaningConfirm] = useState(false);
    const [skipCleaningReason, setSkipCleaningReason] = useState('');
    const useScannedTreatmentText = scanMode
        ? scanMode === 'treatment'
        : isScannedTreatmentStep(currentStepName);
    const isEquipmentScan = !useScannedTreatmentText;
    const resourceLabel = useScannedTreatmentText ? 'Scan Treatment' : 'Equipment ID';
    const resourcePlaceholder = useScannedTreatmentText ? 'Enter or scan treatment' : 'Scan or Enter Equipment ID';
    const pageHeading = useScannedTreatmentText ? 'Scan Treatment' : 'Scan Equipment ID';
    const isCleaningStep = normalizeStepName(currentStepName).includes('clean');

    const getMappedEquipmentName = (typedValue: string) => {
        const normalizedInput = String(typedValue ?? '').trim().toLowerCase();
        if (!normalizedInput || useScannedTreatmentText) return '';

        const matchedEquipment = currentStepEquipments.find(
            (equipment) => String(equipment?.equipment_id ?? '').trim().toLowerCase() === normalizedInput
        );

        return String(matchedEquipment?.display_name ?? '');
    };

    const mappedEquipmentName = getMappedEquipmentName(scannedValue);

    const handleRemoveResource = (resource: string) => {
        setValidResourceList((prev) => prev.filter((r) => r !== resource));
    };

    const handleResourceScan = (value: string) => {
    
        if (value && value.trim()) {
            if (isMultipleResourcesChecked) {
                if (!validResourceList.includes(value.trim())) {
                    setValidResourceList((prev) => [...prev, value.trim()]);
                }
            }
        
        }
    };

    const handleProceed = () => {
        const value = scannedValue.trim();
        if (!value) {
            setErrorText(isEquipmentScan ? 'Equipment is required' : 'Treatment is required');
            return;
        }

        if (isEquipmentScan) {
            const validation = validateEquipment(value, currentStepEquipments);
            if (!validation.valid || !validation.equipmentId) {
                setErrorText(validation.error || 'Invalid equipment');
                setScannedValue('');
                setScanInputKey(k => k + 1);
                return;
            }
            setErrorText('');
            onResourceScanned(validation.equipmentId);
            return;
        }

        // Treatment validation
        if (mappedTreatments.length > 0) {
            const isValid = mappedTreatments.some(
                (t) => t.trim().toLowerCase() === value.toLowerCase()
            );
            if (!isValid) {
                setErrorText(`Invalid treatment. Expected: ${mappedTreatments.join(', ')}`);
                setScannedValue('');
                setScanInputKey(k => k + 1);
                return;
            }
        }

        setErrorText('');
        onResourceScanned(value);
    };

    const handleDone = () => {
        if (validResourceList.length > 0) {
            // For now, just use the first resource ID
            onResourceScanned(validResourceList[0]);
        }
    };

    const handleSkipClick = () => {
        if (!onSkip) return;
        if (isCleaningStep) {
            setShowSkipCleaningConfirm(true);
            return;
        }
        onSkip();
    };

    const handleConfirmSkipCleaning = () => {
        setShowSkipCleaningConfirm(false);
        onSkip?.(skipCleaningReason || undefined);
        setSkipCleaningReason('');
    };

    return (
        <>
        <Box
            sx={{
                width: '100%',
                minHeight: 'calc(100dvh - 140px)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    position: 'sticky',
                    top: 0,
                    zIndex: 2
                }}
            >
                <Divider />
                <Box display={'flex'} width="100%" alignItems={'center'} justifyContent={'flex-start'}>
                    <ArrowBackIosIcon sx={{
                        verticalAlign: 'middle',
                        cursor: 'pointer',
                        fontSize: 28,
                    }} onClick={onBack} />

                    <Typography component="div" sx={{ py: 1.25, px: 1.5 }} className={classes.labelNoto20Bold500_hh}>{pageHeading}</Typography>
                </Box>


                <Divider
                    sx={{
                        borderColor: 'grey.400',
                        borderBottomWidth: '1px',
                        position: 'relative',
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        width: '100%'
                    }}
                />
            </Box>

            <Box sx={{ width: '100%', pt: '16px', px: 2, pb: 1, overflowY: 'auto', flex: 1 }}>
                    <ScanButton />
                    <Stack direction="row" spacing={1} sx={{ width: '100%', mt: '24px', alignItems: 'flex-end' }}>
                        <ScanInput
                            key={scanInputKey}
                            softFocusOnMount
                            submitOnScan={false}
                            scanPickupEnabled={!manualEdit}
                            defaultValue={scannedValue}
                            onChange={(val: string) => {
                                setScannedValue(val);
                                if (errorText) {
                                    setErrorText('');
                                }
                            }}
                            onScan={(val) => {
                                handleResourceScan(val);
                            }}
                            onEnterPress={handleProceed}
                            manualEdit={manualEdit}
                            error={Boolean(errorText?.trim())}
                            label={resourceLabel}
                            placeholder={resourcePlaceholder}
                            errorText={errorText}
                            // triggerValidation={validationTrigger}
                            // onValidationSuccess={(data) => { setValidResourceList(data) }}
                            // isMultipleSPLotsChecked={isMultipleResourcesChecked}
                            // validSpotList={validResourceList}
                            // onDone={(resourceIds) => onResourceScanned(resourceIds[0])}
                        />
                    </Stack>
                    {mappedEquipmentName ? (
                        <Typography component="div" sx={{ mt: 1 }} color="#4A5565" className={classes.labelNoto12Bold500_hh}>
                            {`Equipment Name: ${mappedEquipmentName}`}
                        </Typography>
                    ) : null}

                    {isEquipmentScan && (
                        <Stack
                            direction="row"
                            spacing={0.75}
                            sx={{
                                mt: 2,
                                mb: 1,
                                width: '100%',
                                alignItems: 'stretch',
                            }}
                        >
                            <Button
                                variant="outlined"
                                onClick={onBack}
                                sx={{
                                    textTransform: 'none',
                                    minHeight: 34,
                                    borderRadius: '4px',
                                    borderWidth: '1px',
                                    border: '1px solid #000000',
                                    color: '#000000',
                                    fontSize: '16px',
                                    px: 0.75,
                                    flex: '1 1 0',
                                    minWidth: 0,
                                    whiteSpace: 'nowrap',
                                    '&:hover': {
                                        border: '1px solid #000000',
                                    },
                                }}
                            >
                                Cancel
                            </Button>
                            {!isEquipmentMandatory && (
                                <Button
                                    variant="outlined"
                                    onClick={handleSkipClick}
                                    className={classes.redBorderRedLabel}
                                    startIcon={<SkipNextOutlinedIcon />}
                                    sx={{
                                        minHeight: 34,
                                        height: 34,
                                        fontSize: '16px',
                                        px: 0.75,
                                        flex: '1 1 0',
                                        minWidth: 0,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    Skip
                                </Button>
                            )}
                            <Button
                                variant="contained"
                                onClick={handleProceed}
                                sx={{
                                    textTransform: 'none',
                                    minHeight: 34,
                                    borderRadius: '4px',
                                    fontSize: '16px',
                                    px: 0.75,
                                    background: '#058333',
                                    flex: '1 1 0',
                                    minWidth: 0,
                                    whiteSpace: 'nowrap',
                                    '&:hover': {
                                        background: '#058333',
                                    },
                                }}
                            >
                                Validate
                            </Button>
                        </Stack>
                    )}

                    {!isEquipmentScan && (
                        <Stack
                            direction="row"
                            spacing={0.75}
                            sx={{
                                mt: 2,
                                mb: 1,
                                width: '100%',
                                alignItems: 'stretch',
                            }}
                        >
                            <Button
                                variant="outlined"
                                onClick={onBack}
                                sx={{
                                    textTransform: 'none',
                                    minHeight: 34,
                                    borderRadius: '4px',
                                    borderWidth: '1px',
                                    border: '1px solid #000000',
                                    color: '#000000',
                                    fontSize: '16px',
                                    px: 0.75,
                                    flex: '1 1 0',
                                    minWidth: 0,
                                    whiteSpace: 'nowrap',
                                    '&:hover': {
                                        border: '1px solid #000000',
                                    },
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleProceed}
                                sx={{
                                    textTransform: 'none',
                                    minHeight: 34,
                                    borderRadius: '4px',
                                    fontSize: '16px',
                                    px: 0.75,
                                    background: '#058333',
                                    flex: '1 1 0',
                                    minWidth: 0,
                                    whiteSpace: 'nowrap',
                                    '&:hover': {
                                        background: '#058333',
                                    },
                                }}
                            >
                                Validate
                            </Button>
                        </Stack>
                    )}
                    {/* <Stack direction="column" sx={{ width: "100%", mt: 1 }}>

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isMultipleResourcesChecked}
                                    onChange={(e) => setIsMultipleResourcesChecked(e.target.checked)}
                                />
                            }
                            label="Scan multiple Resources"
                            sx={{
                                '& .MuiFormControlLabel-label': {
                                    fontWeight: 500,
                                    fontSize: 20.8
                                },
                            }}
                            className={classes.labelNoto16Bold500_hh}
                        />
                    </Stack> */}
                    {isMultipleResourcesChecked && <Box>
                        <Stack direction="column" sx={{ width: "100%", mt: 1 }}>
                            <Typography component="div" sx={{ mt: 3 }} className={classes.labelNoto20Bold500_hh}>Equipment IDs ({validResourceList.length})</Typography>
                            <Divider
                                sx={{
                                    borderColor: "grey.400",
                                    borderBottomWidth: "1px",
                                    position: 'relative',
                                    bgcolor: 'background.paper',
                                    borderRadius: 1,
                                    width: "100%"
                                }}
                            />
                        </Stack>

                        <Stack width="100%" display={"flex"} spacing={1} flexDirection={"column"} alignItems={"center"} sx={{ mt: 2 }}>
                            {validResourceList.map((resource) => (
                                <Box key={resource} sx={{ display: 'flex', flexDirection: 'row', backgroundColor: '#F9FAFB', justifyContent: 'space-between', alignItems: 'center', gap: 2,padding:'10px', width: '100%',border:'0.5px solid #E5E7EB',borderRadius:'8px' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                         <img
                                            src={"icons/SP_Lots_Icon_black.svg"}
                                            alt="Resource"
                                            width={19.2}
                                            height={19.2}
                                            style={{ display: "block" }}
                                        />
                                        <Typography component="span" sx={{ml:3}}  className={classes.labelNoto14Bold400_hh}>{resource}</Typography>
                                    </Box>
                                    <IconButton aria-label="close" onClick={() => handleRemoveResource(resource)}>
                                        <CloseIcon sx={{ color: black, fontSize: 21.6 }} />
                                    </IconButton>
                                </Box>
                            ))}
                            <Button variant="contained" className={classes.blueButton_hh} disableRipple   sx={{ height: 40, width: '100%', marginTop: 3 }}
                                startIcon={<CheckCircleIcon sx={{ color: 'white', fontSize: 26 }} />}
                                onClick={handleDone}
                            >Done</Button>
                        </Stack>
                    </Box>}
            </Box>
                </Box>
            <CleaningSkipConfirmDialog
                open={showSkipCleaningConfirm}
                reason={skipCleaningReason}
                onReasonChange={setSkipCleaningReason}
                onCancel={() => {
                    setShowSkipCleaningConfirm(false);
                    setSkipCleaningReason('');
                }}
                onConfirm={handleConfirmSkipCleaning}
            />
        </>
    );
}